import {
  createProjectService,
  deleteProjectService,
  getAllProjectsService,
  getProjectDetailService,
  updateProjectService,
} from "@/service/project.service";
import { ProjectList, ProjectQuery } from "@/types/project.model";
import { ROLES } from "@/utils/constants";
import { successToast } from "@/utils/toast";
import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "@store";
import { selectSelectedOrganization } from "./clientOrganizationSlice";

// Interface for the Project in our Redux store
export interface Project {
  id: string;
  name: string;
  clientId: string;
  clientName: string;
  email?: string;
  startDate: string;
  endDate: string;
  estimatedHours: number;
  utilizedHours?: number;
  remainingHours?: number;
  assignedTo: string[];
  notes?: string;
  status: "Active" | "Completed" | "On Hold" | "Cancelled";
}

// Helper function to convert our Redux model to API request format
export const mapProjectToApi = (project: Project): any => {
  return {
    id: project.id,
    name: project.name,
    client_id: project.clientId,
    email: project.email,
    start_date: project.startDate,
    end_date: project.endDate,
    est_hrs: project.estimatedHours,
    notes: project.notes,
    status:
      project.status === "Active"
        ? "active"
        : project.status === "Completed"
          ? "inactive"
          : "pending",
    user_ids: project.assignedTo,
  };
};

interface ProjectState {
  projects: ProjectList[];
  filteredProjects: ProjectList[];
  noProjects: boolean;
  searchTerm: string;
  statusFilter: string;
  clientId: string;
  warningFilter: string; // "All projects" | "true" | "false" (string form for UI)
  projectTypeFilter: string; // "All project types" | "Billable" | "Non-billable" | "Block of time"
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  loading: boolean;
  error: string | null;
  pagination: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    pageSize: number;
  };
}

const initialState: ProjectState = {
  projects: [],
  noProjects: false,
  filteredProjects: [],
  searchTerm: "",
  clientId: "All clients",
  statusFilter: "All statuses",
  warningFilter: "All projects",
  projectTypeFilter: "All project types",
  sortBy: undefined,
  sortOrder: undefined,
  loading: false,
  error: null,
  pagination: {
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    pageSize: 20,
  },
};

// Async thunks
export const fetchProjects = createAsyncThunk(
  "projects/fetchProjects",
  async (
    query: ProjectQuery = { page: 1, pageSize: 20 },
    { dispatch, getState }
  ) => {
    try {
      dispatch(setLoading(true));

      // Get current filters from state if not provided in query
      const state = getState() as RootState;
      const currentQuery: ProjectQuery = {
        page: query.page || state.projects.pagination.currentPage,
        pageSize: query.pageSize || state.projects.pagination.pageSize,
        search:
          query.search !== undefined
            ? query.search
            : state.projects.searchTerm || undefined,
        status:
          state.projects.statusFilter !== "All statuses"
            ? state.projects.statusFilter.toLowerCase()
            : undefined,
        clientId:
          state.projects.clientId !== "All clients"
            ? state.projects.clientId
            : undefined,
        // map additional filters
        warning:
          state.projects.warningFilter === "true"
            ? true
            : state.projects.warningFilter === "false"
              ? false
              : undefined,
        projectType:
          state.projects.projectTypeFilter !== "All project types"
            ? (state.projects.projectTypeFilter.toLowerCase() as any)
            : undefined,
        sortBy: state.projects.sortBy,
        sortOrder: state.projects.sortOrder,
      };

      const roles = state.auth.roles;
      const userRole = state.auth.user?.role_code as string;

      const inverted = Object.fromEntries(
        Object.entries(ROLES).map(([key, value]) => [value, key])
      );

      const role_id = roles.find(
        (role) => role.name === inverted[userRole]
      )?.id;

      if (role_id && userRole !== ROLES.ADMIN) {
        currentQuery.role = role_id;
      }

      // For client users, use the selected organization's client_id
      if (userRole === ROLES.CLIENT) {
        const selectedOrg = selectSelectedOrganization(state);
        if (selectedOrg?.client_id) {
          currentQuery.clientId = selectedOrg.client_id;
        }
      }

      const {
        data: { data, pagination },
      } = await getAllProjectsService(currentQuery);

      dispatch(setNoProjects(false));

      return {
        projects: data,
        pagination: {
          currentPage: pagination?.page as number,
          totalPages: pagination?.totalPages as number,
          totalItems: pagination?.total as number,
          pageSize: pagination?.pageSize as number,
        },
      };
    } catch (error: any) {
      if (error.response?.status === 404) {
        dispatch(setNoProjects(true));
        throw error;
      }

      const message =
        error.response?.data?.message || "Failed to fetch projects";
      dispatch(setError(message));
      throw error;
    } finally {
      dispatch(setLoading(false));
    }
  }
);

export const getProjectDetail = createAsyncThunk(
  "projects/getProjectDetail",
  async (projectId: string, { dispatch }) => {
    try {
      dispatch(setLoading(true));
      const { data } = await getProjectDetailService(projectId);
      // Map API response to our Redux model
      return data.data;
    } catch (error: any) {
      const message =
        error.response?.data?.message || "Failed to fetch project details";
      dispatch(setError(message));
      throw error;
    } finally {
      dispatch(setLoading(false));
    }
  }
);

export const addProject = createAsyncThunk(
  "projects/addProject",
  async (project: ProjectList, { dispatch, getState }) => {
    try {
      dispatch(setLoading(true));

      await createProjectService(project);
      successToast("Project added successfully", "");

      // Refresh the projects list after adding
      const state = getState() as RootState;
      dispatch(
        fetchProjects({
          page: state.projects.pagination.currentPage,
          pageSize: state.projects.pagination.pageSize,
        })
      );

      // Map API response back to our Redux model
      return project;
    } catch (error: any) {
      const message = error.response?.data?.message || "Failed to add project";
      dispatch(setError(message));
      throw error;
    } finally {
      dispatch(setLoading(false));
    }
  }
);

export const updateProject = createAsyncThunk(
  "projects/updateProject",
  async (project: ProjectList, { dispatch, getState }) => {
    try {
      dispatch(setLoading(true));

      await updateProjectService(project.id, project);
      successToast("Project updated successfully", "");

      // Refresh the projects list after updating
      const state = getState() as RootState;
      dispatch(
        fetchProjects({
          page: state.projects.pagination.currentPage,
          pageSize: state.projects.pagination.pageSize,
        })
      );

      return project;
    } catch (error: any) {
      const message =
        error.response?.data?.message || "Failed to update project";
      dispatch(setError(message));
      throw error;
    } finally {
      dispatch(setLoading(false));
    }
  }
);

export const deleteProject = createAsyncThunk(
  "projects/deleteProject",
  async (projectId: string, { dispatch, getState }) => {
    try {
      dispatch(setLoading(true));

      await deleteProjectService(projectId);

      successToast("Project deleted successfully", "");

      // Refresh the projects list after deletion
      const state = getState() as RootState;
      dispatch(
        fetchProjects({
          page: state.projects.pagination.currentPage,
          pageSize: state.projects.pagination.pageSize,
        })
      );

      return projectId;
    } catch (error: any) {
      const message =
        error.response?.data?.message || "Failed to delete project";
      dispatch(setError(message));
      throw error;
    } finally {
      dispatch(setLoading(false));
    }
  }
);

const projectSlice = createSlice({
  name: "projects",
  initialState,
  reducers: {
    setNoProjects(state, action: PayloadAction<boolean>) {
      state.noProjects = action.payload;
    },
    setStatusFilter(state, action: PayloadAction<string>) {
      state.statusFilter = action.payload;
    },
    setClientId(state, action: PayloadAction<string>) {
      state.clientId = action.payload;
    },
    resetFilters(state) {
      state.searchTerm = "";
      state.clientId = "All clients";
      state.statusFilter = "All statuses";
      state.warningFilter = "All projects";
      state.warningFilter = "All projects";
      state.projectTypeFilter = "All project types";
      state.sortBy = undefined;
      state.sortOrder = undefined;
    },
    setSort(state, action: PayloadAction<{ sortBy: string; sortOrder: "asc" | "desc" }>) {
      state.sortBy = action.payload.sortBy;
      state.sortOrder = action.payload.sortOrder;
    },
    setLoading(state, action: PayloadAction<boolean>) {
      state.loading = action.payload;
    },
    setError(state, action: PayloadAction<string | null>) {
      state.error = action.payload;
    },
    setSearchTerm: (state, action: PayloadAction<string>) => {
      state.searchTerm = action.payload;
    },
    setWarningFilter: (state, action: PayloadAction<string>) => {
      state.warningFilter = action.payload;
    },
    setProjectTypeFilter: (state, action: PayloadAction<string>) => {
      state.projectTypeFilter = action.payload;
    },
    resetAllState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      // Fetch projects
      .addCase(fetchProjects.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProjects.fulfilled, (state, action) => {
        state.loading = false;
        state.projects = action.payload.projects;
        state.filteredProjects = action.payload.projects;
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchProjects.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to fetch projects";
      })
      .addCase(addProject.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addProject.fulfilled, (state) => {
        // We don't need to update the state manually here since fetchUsers will be called
        state.loading = false;
      })
      .addCase(addProject.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to add project";
      })
      .addCase(updateProject.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateProject.fulfilled, (state) => {
        // We don't need to update the state manually here since fetchUsers will be called
        state.loading = false;
      })
      .addCase(updateProject.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to update project";
      })
      .addCase(deleteProject.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteProject.fulfilled, (state) => {
        // We don't need to update the state manually here since fetchUsers will be called
        state.loading = false;
      })
      .addCase(deleteProject.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to delete project";
      })
      .addCase(getProjectDetail.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getProjectDetail.fulfilled, (state) => {
        // We don't need to update the state manually here since fetchUsers will be called
        state.loading = false;
      })
      .addCase(getProjectDetail.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to fetch project details";
      });
  },
});

export const {
  setLoading,
  setError,
  setSearchTerm,
  setStatusFilter,
  setClientId,
  resetFilters,
  resetAllState,
  setNoProjects,
  setWarningFilter,
  setProjectTypeFilter,
  setSort,
} = projectSlice.actions;

// Selectors
export const selectProjects = (state: RootState) => state.projects.projects;
export const selectFilteredProjects = (state: RootState) =>
  state.projects.filteredProjects;
export const selectSearchTerm = (state: RootState) => state.projects.searchTerm;
export const selectStatusFilter = (state: RootState) =>
  state.projects.statusFilter;
export const selectProjectLoading = (state: RootState) =>
  state.projects.loading;
export const selectProjectError = (state: RootState) => state.projects.error;
export const selectClientId = (state: RootState) => state.projects.clientId;
export const selectPagination = (state: RootState) => state.projects.pagination;
export const selectNoProjects = (state: RootState) => state.projects.noProjects;
export const selectWarningFilter = (state: RootState) =>
  state.projects.warningFilter;
export const selectProjectTypeFilter = (state: RootState) =>
  state.projects.projectTypeFilter;
export const selectSortBy = (state: RootState) => state.projects.sortBy;
export const selectSortOrder = (state: RootState) => state.projects.sortOrder;

export default projectSlice.reducer;
